<template>
    <div v-if="paygateMethod">
        <div class="paygate-hero">
            <h4 class="paygate-hero-title">Pay by Card or PayPal</h4>
            <p class="paygate-hero-sub">
                Pay with your card, PayPal, or bank through our secure payment partner.
                Pick an amount and you'll be taken to a secure checkout — nothing to copy or paste.
                Includes a <strong>10% bonus</strong> on your credits.
            </p>
        </div>

        <b-alert v-if="error" show variant="danger" class="mt-3">
            <strong>Something went wrong.</strong>
            We couldn't start your payment. Please try again in a few moments.
        </b-alert>

        <div v-if="!price">
            <refill-prices :prices="creditRefillPricesCrypto" />
        </div>

        <template v-else>
            <div class="amount-summary">
                <div>
                    <div class="amount-summary-label">You're buying</div>
                    <div class="amount-summary-credits">
                        {{ formatNumber(price.totalCredits) }} credits
                    </div>
                </div>
                <div class="text-right">
                    <div class="amount-summary-price">${{ price.amount }}</div>
                    <b-btn variant="link" size="sm" class="change-amount" @click="changeAmount">
                        Change amount
                    </b-btn>
                </div>
            </div>

            <b-btn
                block
                size="lg"
                variant="primary"
                pill
                class="pay-btn"
                :disabled="loading"
                @click="continueToPayment"
            >
                <b-spinner v-if="loading" small class="mr-2" />
                <font-awesome-icon v-else icon="fa-solid fa-lock" class="mr-2" />
                {{ loading ? 'Opening secure checkout…' : 'Continue to Payment' }}
            </b-btn>

            <div class="pay-tip">
                <font-awesome-icon icon="fa-solid fa-shield-check" class="mr-1" />
                Card, PayPal &amp; bank options are shown based on your country.
                Credits arrive within a few minutes.
            </div>
        </template>
    </div>
</template>

<script>
import { mapState } from "vuex";

import RefillPrices from "@/components/wallet/refill/RefillPrices";

export default {
    data() {
        return {
            loading: false,
            error: false,
        };
    },
    computed: {
        ...mapState("wallet", ["price"]),
        ...mapState("paymentMethods", {
            paymentMethods: "methods",
        }),
        ...mapState("options", ["creditRefillPricesCrypto"]),
        paygateMethod() {
            return this.paymentMethods?.find((pm) => pm.type === "paygate");
        },
    },
    methods: {
        formatNumber(value) {
            return Number(value).toLocaleString();
        },
        changeAmount() {
            this.$store.commit("wallet/setPrice", null);
            this.error = false;
        },
        continueToPayment() {
            this.error = false;
            this.loading = true;

            this.api
                .post("/paygate/checkout", { priceId: this.price.id })
                .then((response) => {
                    const url = response?.data?.url;

                    if (!url) {
                        throw new Error("no url");
                    }

                    // Platform handler — Cordova InAppBrowser (_system) on app, window.open on web.
                    this.$store.dispatch("system/openBrowser", url);
                })
                .catch(() => {
                    this.error = true;
                })
                .then(() => {
                    this.loading = false;
                });
        },
    },
    components: {
        RefillPrices,
    },
};
</script>

<style scoped>
.paygate-hero {
    text-align: center;
    padding: 1.5rem 0 1rem;
}

.paygate-hero-title {
    font-weight: 700;
    letter-spacing: -0.01em;
    margin-bottom: 0.5rem;
}

.paygate-hero-sub {
    color: var(--secondary, #6c757d);
    font-size: 1rem;
    line-height: 1.5;
    max-width: 480px;
    margin: 0 auto;
}

.amount-summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: linear-gradient(135deg, rgba(232, 69, 124, 0.06), rgba(244, 162, 97, 0.06));
    border: 1px solid rgba(232, 69, 124, 0.18);
    border-radius: 14px;
    padding: 0.85rem 1.1rem;
    margin: 1.25rem 0;
}

.amount-summary-label {
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--secondary, #6c757d);
}

.amount-summary-credits {
    font-size: 1.15rem;
    font-weight: 700;
}

.amount-summary-price {
    font-size: 1.3rem;
    font-weight: 700;
}

.change-amount {
    padding: 0;
    font-size: 0.8rem;
    font-weight: 600;
    text-decoration: none;
}

.pay-btn {
    font-weight: 700;
    padding-top: 0.85rem;
    padding-bottom: 0.85rem;
}

.pay-tip {
    margin-top: 0.75rem;
    font-size: 0.85rem;
    color: var(--secondary, #6c757d);
    text-align: center;
}
</style>
